import { useEffect, useState } from "react";

/**
 * The key used to persist the user's cookie-preference choice.
 *
 * NOTE: diagnosticsAllowed() has intentionally been moved to src/lib/sentry.ts
 * and is no longer exported from this file.  Keeping it here would create a
 * circular dependency between the Sentry initialisation module (which runs
 * before React mounts) and this React component module, pulling the entire
 * legal subtree into the synchronous startup bundle and defeating code-splitting.
 *
 * If you need to read the preference elsewhere, read localStorage directly:
 *   localStorage.getItem("mbaay_cookie_preferences") === "accepted"
 */
const KEY = "mbaay_cookie_preferences";
type Preference = "accepted" | "rejected" | null;

export default function CookiePreferences() {
  const [choice, setChoice] = useState<Preference>(
    () => localStorage.getItem(KEY) as Preference,
  );
  const [open, setOpen] = useState(!choice);

  useEffect(() => {
    const manage = () => setOpen(true);
    window.addEventListener("mbaay:manage-cookie-preferences", manage);
    return () =>
      window.removeEventListener("mbaay:manage-cookie-preferences", manage);
  }, []);

  const save = (value: Exclude<Preference, null>) => {
    localStorage.setItem(KEY, value);
    setChoice(value);
    setOpen(false);
    // A full reload is necessary so that Sentry re-initialises with the new
    // diagnosticsAllowed() result — the SDK reads this synchronously at startup.
    window.location.reload();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-slate-300 bg-white p-4 shadow-2xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-title"
    >
      <div className="mx-auto max-w-5xl">
        <h2 id="cookie-title" className="text-lg font-bold">
          Cookie preferences
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-700">
          Essential browser storage supports sign-in, carts and settings. With
          your permission, Mbaay may enable Sentry diagnostics to identify
          errors. We do not enable optional diagnostics until you choose.
        </p>
        <div className="mt-3 flex flex-wrap gap-3">
          <button
            onClick={() => save("rejected")}
            className="min-h-11 rounded border border-slate-600 px-4 font-semibold"
          >
            Reject diagnostics
          </button>
          <button
            onClick={() => save("accepted")}
            className="min-h-11 rounded bg-orange-600 px-4 font-semibold text-white"
          >
            Allow diagnostics
          </button>
          <a
            className="min-h-11 pt-2 text-sm underline"
            href="/cookie-policy"
          >
            Read cookie policy
          </a>
        </div>
      </div>
    </div>
  );
}
